import { forwardRef } from 'react';
import { cn } from '../../lib/cn';

export const Card = forwardRef(function Card(
  {
    // eslint-disable-next-line no-unused-vars
    as: Component = 'div',
    className,
    style,
    interactive = false,
    glow = false,
    children,
    ...props
  },
  ref
) {
  return (
    <Component
      ref={ref}
      className={cn(
        'relative rounded-2xl border transition-colors',
        interactive && 'cursor-pointer hover:bg-[var(--dash-surface-hover)]',
        className
      )}
      style={{
        background: 'var(--dash-surface)',
        borderColor: 'var(--dash-border)',
        boxShadow: glow
          ? '0 0 0 1px var(--dash-primary-border), 0 12px 32px -12px rgba(173,52,254,0.25)'
          : 'var(--dash-shadow-sm)',
        ...style,
      }}
      {...props}
    >
      {children}
    </Component>
  );
});

export function CardHeader({ className, children, ...props }) {
  return (
    <div
      className={cn('flex flex-col gap-1.5 px-6 pt-6 pb-4', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardTitle({ className, children, ...props }) {
  return (
    <h3
      className={cn('text-base font-semibold tracking-tight', className)}
      style={{ color: 'var(--dash-text)' }}
      {...props}
    >
      {children}
    </h3>
  );
}

export function CardDescription({ className, children, ...props }) {
  return (
    <p
      className={cn('text-xs leading-relaxed', className)}
      style={{ color: 'var(--dash-text-muted)' }}
      {...props}
    >
      {children}
    </p>
  );
}

export function CardContent({ className, children, ...props }) {
  return (
    <div className={cn('px-6 pb-6', className)} {...props}>
      {children}
    </div>
  );
}

export function CardFooter({ className, children, ...props }) {
  return (
    <div
      className={cn('flex items-center gap-2 border-t px-6 py-4', className)}
      style={{ borderColor: 'var(--dash-border)' }}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardSection({ className, children, title, description, action, ...props }) {
  return (
    <Card className={cn('overflow-hidden', className)} {...props}>
      {(title || description || action) && (
        <CardHeader className="flex-row items-start justify-between gap-4 pb-2">
          <div className="flex flex-col gap-1">
            {title && <CardTitle>{title}</CardTitle>}
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </CardHeader>
      )}
      <CardContent className="pt-2">{children}</CardContent>
    </Card>
  );
}
